import { defineComponent } from "vue";
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';;
import NavBar from "@/plugins/nav-bar/nav-bar.vue";
import { usePortalStore } from "@/framework/portal";
import _ from "lodash";
import { portalPageConfig } from "./portalPageConfig";
import { useI18n } from 'vue-i18n';
import { computed } from "@vue/reactivity";

function getStorageKey(tenantId: string, profileName: string): string {
	// Scope by tenant+profile to prevent cross-portal bleed when the same browser is used with different tenant or profile combinations
	return `portal_language_${tenantId}_${profileName}`;
}

export default defineComponent({
	name: "App",
	components: {
		NavBar
	},
	setup() {
		const $q = useQuasar();
		const portal = usePortalStore();
        const { t, setLocaleMessage, locale } = useI18n();
		
		$q.dark.set(true);
		$q.dark.toggle();
		portal._quasar = $q;
		portal._translate = t;

		return {
			portal,
			setLocaleMessage,
			locale
		};
	},
	data: () => ({
		locales: [] as any[string],
		stepNavigation: [] as any[],
		urlParams: null as any,
		selectedLocale: "" as string,
		loadedLocales: [] as any[string],
		localesLoaded: false as boolean,
		linkLoaded: false as boolean,
		profileName: "" as string,
		tenantId: "" as string
	}),
	watch: {
		selectedLocale(val) {
			localStorage.setItem(getStorageKey(this.tenantId, this.profileName), val);
			if (!this.loadedLocales.includes(val))
				this.fetchLanguages(val);
			else {
				this.locale = val;
			}
		}
	},
	created() {
		const queryString = window.location.search;
		const searchParams = new URLSearchParams(queryString);
		this.urlParams = new URLSearchParams([...searchParams].map(([key, value]) => [key.toLowerCase(), value]));
		this.profileName = this.urlParams.get("profilename") || null;
		this.tenantId = this.urlParams.get("tenant") || null;
		this.portal.profileName = this.profileName;
		this.portal.tenantId = this.tenantId;
		this.portal.idParam = this.urlParams.get("id");
		this.portal.referrer.visitorSelfEntry = this.urlParams.get("referrer")?.toLowerCase() === "visitorselfentry";
		this.portal.referrer.name = this.urlParams.get("referrer")?.toLowerCase() ?? null;
		this.portal.isAppReady = false;

		this.fetchStyles();

		this.$q.loading.show();
		portalPageConfig.registerErrorPageConfig();
		
		if (!this.portal.idParam) {
			this.$q.loading.hide();
			// should we show the error page here since there is no id?
			return;
		}

		this.fetchConfig();
	},
	methods: {
		fetchConfig() {
			fetch(`${this.portal.getApiPath()}externalPortal/getConfig/${this.tenantId}/${this.profileName}`)
				.then((r) => r.json())
				.then((config) => {
					this.locales = _.map(config.languages, 'language');
					
					const paramLang = this.urlParams.get("language");
					const defaultLanguage = _.head(_.filter(config.languages, loc => {return loc.isDefault === true})).language;
					const storageKey = getStorageKey(this.tenantId, this.profileName);

					if (paramLang &&  this.locales.some((l: string) => l.toLowerCase() === paramLang.toLowerCase())) {
						// Overwrite the stored value
						localStorage.setItem(storageKey, paramLang);
					} 
					
					// Get the stored language value and check it is valid for the profile config
					let storedLang = localStorage.getItem(storageKey);
					if (storedLang && !this.locales.some((l: string) => l.toLowerCase() === storedLang!.toLowerCase())) {
						// if stored language is not available anymore, clear it & let language default
						localStorage.removeItem(storageKey);
						storedLang = null;
					}

					// 1. language from localStorage (language from queryString is set to localStorage as 1st preference, if valid)
					// 2. configured default language 
					// 3. last resort, fallback to English
					const resolvedLanguage = storedLang || defaultLanguage || "en";

					this.fetchLanguages(resolvedLanguage);
					this.fetchData();
					return;
				}).catch((error) => {
					this.portal._quasar.loading.hide();
					this.portal.currentNavIndex = 0;
					this.portal.isAppReady = true;
					if (error) console.log("Error:", error);
				});
		},
		fetchLanguages(lang: string) {
			fetch(`${this.portal.getApiPath()}externalPortal/getLanguage/${this.tenantId}/${lang}?profileName=${this.profileName}`)
				.then((r) => r.json())
				.then((json) => {
					this.setLocaleMessage(lang, json);
					this.loadedLocales.push(lang);
					this.selectedLocale = lang;
					this.locale = lang;
					this.localesLoaded = true;
					this.portal.isAppReady = this.linkLoaded && this.localesLoaded;	
					if (this.portal.isAppReady)
						this.$q.loading.hide();
					
					return;
				}).catch((error) => {
					this.portal._quasar.loading.hide();
					this.portal.currentNavIndex = 0;
					this.portal.isAppReady = true;
					if (error) console.log("Error:", error);
				});
		},
		fetchData() {
			const apiUrl = `${this.portal.getApiPath()}externalPortal/getExternalPortal/${this.tenantId}/${this.urlParams.get("id")}/${this.profileName}`;

			fetch(apiUrl) 
				.then((r) => r.json())
				.then((json) => {
					this.portal.originalPayloadData = json;
					
					// LinkStatus 
					// Valid: New = 0, Valid = 1
					// Invalid: Expired = 2, NotFound = 3, Deleted = 4, Completed = 5, Invalid = 6			
					if (json.linkStatus <= 1) {
						// Valid
						this.portal.payload = json.portalPayload.payload;
						this.portal.visitDetails = (<any>this.portal).payload.visitDetails;
						this.portal.photoDetails = (<any>this.portal).payload.photoDetails;
						this.portal.photoValidationEnabled = (<any>this.portal).payload.photoValidationEnabled;
						
						this.portal.fields = (<any>this.portal).payload.fields;
						this.formatFieldsData(this.portal.fields);
						this.portal.inductions = (<any>this.portal).payload.inductions;

						const referrersWhoCanSkip: string = (<any>this.portal).payload.referrersSkipDataPolicy?.toLowerCase();
						if (this.portal.referrer.name && referrersWhoCanSkip?.indexOf(this.portal.referrer.name) > -1) {
							this.portal.dataPolicyRequired = false;
						}

						this.portal.visitCodeQr = {
							enabled: (<any>this.portal).payload.showQrCode ?? true,
							generatorUrl: (<any>this.portal).payload.QrGeneratorUrl ?? '',
							width: (<any>this.portal).payload.QrWidth ?? null
						};

						this.portal.cdfRefMap = {
							visitReason: (<any>this.portal).payload.visitReasonCdf,
							visitorType: (<any>this.portal).payload.visitorTypeCdf
						};

						portalPageConfig.registerPageConfig();
					}
					else {
						portalPageConfig.registerPageConfig();
						if (json.linkStatus === 5) {
							this.portal.currentNavIndex = this.portal.getNavIndexFromPageName('completeStatusPage');
						} else {				
							// Invalid status - Set error message
							if (json.linkStatus === 2) {
								this.portal.errorDescription = "portal_errorLinkExpired";
							} else if (json.linkStatus === 3) {
								this.portal.errorDescription = "portal_errorLinkNotFound"; 
							} else if (json.linkStatus === 4) {
								this.portal.errorDescription = "portal_errorLinkDeleted"; 
							} else if (json.linkStatus === 6) {
								this.portal.errorDescription = "portal_errorLinkInvalid"; 
							} 
							// Set error page index as current page
							this.portal.currentNavIndex = 0;
						}
						this.portal.disableHomeButtonClick = true;
					}
			
					this.linkLoaded = true;
					this.portal.isAppReady = this.linkLoaded && this.localesLoaded;	
					if (this.portal.isAppReady)
						this.$q.loading.hide();	
					return;
				}).catch((error) => {
					this.portal._quasar.loading.hide();
					this.portal.currentNavIndex = 0;
					this.portal.isAppReady = true;
					if (error) console.log("Error:", error);
				});
			
		},
		fetchStyles() {
			fetch(`${this.portal.getApiPath()}externalPortal/getStyles/${this.tenantId}/${this.profileName}`)
				.then((r) => r.text())
				.then((customStyles) => {
					const style = document.createElement("style");
					style.innerHTML = customStyles || "";
					document.head.appendChild(style);
					return;
				}).catch((error) => {
					if (error) console.log("Error:", error);
				});
		},
		next(): void {
			const self = this;
			if (this.portal.navPageConfig[this.portal.currentNavIndex].continue) {
				this.portal.navPageConfig[this.portal.currentNavIndex].continue();
				setTimeout(function(){ 
					if (self.portal.isValid && self.portal.currentNavIndex > 0) {
						self.portal.currentNavIndex++;
						window.scrollTo(0,0);	
					}
				}, 100);
			}
			else {
				this.portal.currentNavIndex++;
				window.scrollTo(0,0);	
			}	
		},
		back() {
			this.portal.disableNext = false;
			this.portal.isValid = true;
			window.scrollTo(0,0);
			this.portal.currentNavIndex--;
		},
		skip(): void {
			this.portal.isValid = true;
			if (this.portal.navPageConfig[this.portal.currentNavIndex].skip) {
				this.portal.navPageConfig[this.portal.currentNavIndex].skip();
			}
			window.scrollTo(0,0);	
			this.portal.currentNavIndex++;
		},
		refresh(): void {
			location.reload();
		},
		formatFieldsData(fields: any[]) {
			_.forEach(fields, fld => {
				if (fld.typeId === 6 && fld.subTypeId === 1 && fld.originalValue === null) {
					fld.originalValue = 'off';
				}
				fld["payloadValue"] = fld.originalValue;
				fld["isModified"] = computed(() => {
					if (fld.typeId === 2 && fld.subTypeId === 7 && Array.isArray(fld.originalValue)) {
						return fld.payloadValue.length !== fld.originalValue.length ||
							_.filter(fld.payloadValue, val => !_.includes(fld.originalValue, val)).length != 0;
					}
					else if ((fld.typeId === 5 || fld.typeId === 3) && fld.payloadValue === null && fld.originalValue === '') {
						return false;
					}					
					return !(fld.payloadValue === null && fld.originalValue === '') && fld.payloadValue !== fld.originalValue;
				});
			});
		},
		goToRightCrowd() {
			window.open('https://www.rightcrowd.com/','_blank');
		}
	}
});
